import {ActionList, ActionMenu, FormControl, IconButton} from '@primer/react'
import {TrashIcon, KebabHorizontalIcon} from '@primer/octicons-react'
import {BypassAvatar} from '../BypassAvatar'
import {ActorType} from '../ActorType'
import {ActorBypassMode, type BypassActor} from '../../bypass-actors-types'
import {UNSUPPORTED_BYPASS_MODES_BY_ACTOR_TYPE} from '../../helpers/constants'

import styles from './BypassListRow.module.css'

type BypassActorMode = {
  type: ActorBypassMode
  text: string
  description: string
  shortName: string
}

const bypassActorModeArray: BypassActorMode[] = [
  {
    type: ActorBypassMode.ALWAYS,
    text: 'Always',
    description: 'The ruleset will be evaluated and the selected actor(s) will be prompted to bypass',
    shortName: 'Always allow',
  },
  {
    type: ActorBypassMode.PRS_ONLY,
    text: 'For pull requests only',
    description:
      'The ruleset will be enforced on command line changes and the selected actor(s) will be prompted to bypass only in a pull request',
    shortName: 'Allow for pull requests only',
  },
  {
    type: ActorBypassMode.EXEMPT,
    text: 'Exempt',
    description: 'The ruleset will not be evaluated and no bypass prompt will be shown',
    shortName: 'Exempt from rules',
  },
]

export function BypassListRow({
  actor,
  baseAvatarUrl,
  readOnly,
  removeBypassActor,
  refocusOnRemoval,
  updateBypassActor,
  supportedBypassModes,
}: {
  actor: BypassActor
  baseAvatarUrl: string
  readOnly?: boolean
  removeBypassActor: (bypassActor: BypassActor) => void
  refocusOnRemoval?: () => void
  updateBypassActor: (bypassActor: BypassActor) => void
  supportedBypassModes: ActorBypassMode[]
}) {
  function getSelectedMode(currentActor: BypassActor, getType: boolean) {
    const selectedMode = bypassActorModeArray.find(mode => mode.type === currentActor.bypassMode)
    if (getType) {
      return selectedMode?.type
    } else {
      return selectedMode?.shortName
    }
  }

  function modeIsSelected(currentActor: BypassActor, option: BypassActorMode) {
    const comparisonValue = getSelectedMode(currentActor, true)
    return comparisonValue === option.type
  }

  function humanizeRoleName(currentActor: BypassActor) {
    if (currentActor.actorType === 'RepositoryRole') {
      switch (currentActor.name) {
        case 'admin':
          return 'Repository admin'
        case 'maintain':
          return 'Maintain'
        case 'write':
          return 'Write'
        default:
          return currentActor.name
      }
    }
    return currentActor.name
  }

  function actorMetadata({actorType, name, owner}: BypassActor) {
    if (actorType === 'Team') {
      return <span className="note"> &bull; {`@${name}`} </span>
    }
    if (actorType === 'Integration' && owner) {
      return <span className="note"> &bull; {owner} </span>
    }
    return null
  }

  return (
    <li key={actor._id} className="Box-row d-flex flex-justify-between flex-items-center py-2">
      <div className="d-flex flex-items-center gap-3">
        <BypassAvatar
          baseUrl={baseAvatarUrl}
          id={actor.actorId}
          name={humanizeRoleName(actor)}
          type={actor.actorType}
          preferredAvatarUrl={actor.preferredAvatarUrl}
        />
        <div>
          {humanizeRoleName(actor)}
          <ActorType actorType={actor.actorType} />
          {actorMetadata(actor)}
        </div>
      </div>
      <div className={`d-flex pr-pl-3 flex-items-center`}>
        {
          // eslint-disable-next-line @eslint-react/no-leaked-conditional-rendering
          getSelectedMode(actor, false) &&
            (!readOnly ? (
              <RowRightHalfUnboxed
                actor={actor}
                updateBypassActor={updateBypassActor}
                supportedBypassModes={supportedBypassModes}
                getSelectedMode={getSelectedMode}
                modeIsSelected={modeIsSelected}
                removeBypassActor={removeBypassActor}
                refocusOnRemoval={refocusOnRemoval}
              />
            ) : (
              <>{getSelectedMode(actor, false)}</>
            ))
        }
      </div>
    </li>
  )
}

export function BypassActorMenu({
  actor,
  updateBypassActor,
  supportedBypassModes,
  modeIsSelected,
}: {
  actor: BypassActor
  updateBypassActor: (bypassActor: BypassActor) => void
  supportedBypassModes: ActorBypassMode[]
  modeIsSelected: (currentActor: BypassActor, option: BypassActorMode) => boolean
}) {
  return (
    <>
      {bypassActorModeArray
        .filter(mode => supportedBypassModes.includes(mode.type))
        .map(option => (
          <ActionList.Item
            key={option.type}
            selected={modeIsSelected(actor, option)}
            onSelect={() => {
              updateBypassActor({...actor, bypassMode: option.type, _dirty: true})
            }}
          >
            <div className={styles.ActionListItemText}>{option.text}</div>
            <ActionList.Description variant="block">{option.description}</ActionList.Description>
          </ActionList.Item>
        ))}
    </>
  )
}

function RowRightHalfUnboxed({
  actor,
  updateBypassActor,
  supportedBypassModes,
  getSelectedMode,
  modeIsSelected,
  removeBypassActor,
  refocusOnRemoval,
}: {
  actor: BypassActor
  updateBypassActor: (bypassActor: BypassActor) => void
  supportedBypassModes: ActorBypassMode[]
  getSelectedMode: (currentActor: BypassActor, getType: boolean) => string | ActorBypassMode | undefined
  modeIsSelected: (currentActor: BypassActor, option: BypassActorMode) => boolean
  removeBypassActor: (bypassActor: BypassActor) => void
  refocusOnRemoval?: () => void
}) {
  const supportedModes = supportedBypassModes.filter(
    mode => !UNSUPPORTED_BYPASS_MODES_BY_ACTOR_TYPE[actor.actorType]?.includes(mode),
  )
  const bypassModeSelector = supportedModes.length > 1

  return (
    <>
      {bypassModeSelector && <span className={styles.Text}>{getSelectedMode(actor, false)}</span>}
      <FormControl>
        {bypassModeSelector && <FormControl.Label visuallyHidden>Select bypass mode</FormControl.Label>}
        <ActionMenu>
          <ActionMenu.Anchor>
            <IconButton
              icon={KebabHorizontalIcon}
              variant="invisible"
              aria-label={`${actor.name} Bypass actor actions`}
              description="Bypass actor actions"
              className={styles.IconButton}
            />
          </ActionMenu.Anchor>

          <ActionMenu.Overlay width="xlarge">
            <ActionList>
              {bypassModeSelector && (
                <>
                  <ActionList.Group selectionVariant="single">
                    <BypassActorMenu
                      actor={actor}
                      updateBypassActor={updateBypassActor}
                      supportedBypassModes={supportedModes}
                      modeIsSelected={modeIsSelected}
                    />
                  </ActionList.Group>
                  <ActionList.Divider />
                </>
              )}
              <ActionList.Item
                variant="danger"
                onSelect={() => {
                  refocusOnRemoval?.()
                  removeBypassActor(actor)
                }}
              >
                <ActionList.LeadingVisual>
                  <TrashIcon />
                </ActionList.LeadingVisual>
                Delete bypass
              </ActionList.Item>
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      </FormControl>
    </>
  )
}
